import type {
  Agreements,
  AllowedUseSearchFilterEnum,
  ImageSizeSearchFilterEnum,
  LicenseSearchFilterEnum,
  OfflineContentSearchFilterEnum,
  PhraseProcessingSearchFilterEnum,
  ReleaseStatusSearchFilterEnum,
} from './filterValues';
import {
  AgeOfPeopleEnum,
  licensesOf,
  AssetFamilySearchFilterEnum,
  AssetTypeSearchFilterEnum,
  IstockCollectionSearchFilterEnum,
  ColorMoodEnum,
  CompositionSearchFilterEnum,
  EditorialProductEnum,
  EthnicityEnum,
  FileTypeSearchFilterEnum,
  FilmDefinitionSearchFilterEnum,
  FrameRateEnum,
  ImageTechniqueEnum,
  MediaTypeSearchFilterEnum,
  NumberOfPeopleEnum,
  OrientationEnum,
  SortEnum,
  VideoTemplateAppVersionEnum,
  VideoTemplateCategoryEnum,
  RecencyEnum,
  ViewPointEnum,
} from './filterValues';
import { SCHEMA_DEFAULTS } from './schema';
import type {
  SchemaDefaults,
  SearchParameterElement,
  SearchParameterName,
  SearchParameterValues,
} from './schema';
import {
  clearedValues,
  definedValues,
  isValueSelected,
  setValue,
  toggledValue,
  withValues,
} from './values';

export class SearchParameters {
  readonly #stated: SearchParameterValues;
  readonly #withDefaults: SchemaDefaults & SearchParameterValues;

  constructor(values: SearchParameterValues) {
    const stated = definedValues(values);
    this.#stated = Object.freeze(stated);
    this.#withDefaults = Object.freeze({ ...SCHEMA_DEFAULTS, ...stated });
  }

  toJSON = (): Readonly<SearchParameterValues> => this.#stated;

  get family(): AssetFamilySearchFilterEnum {
    return this.#withDefaults.family;
  }

  get assettype(): AssetTypeSearchFilterEnum {
    return this.#withDefaults.assettype;
  }

  // True when the query is a search-by-image (reverse-image) search.
  get searchByImage(): boolean {
    return !!this.#withDefaults.searchbyimage;
  }

  get agreements(): Agreements | undefined {
    return this.#withDefaults.agreements;
  }

  // Some or none, without the caller naming the four kinds.
  get hasAgreements(): boolean {
    return Object.values(this.#withDefaults.agreements ?? {}).some(Boolean);
  }

  get mediaType(): MediaTypeSearchFilterEnum | undefined {
    return this.#withDefaults.mediatype;
  }

  get assetFileType(): FileTypeSearchFilterEnum | undefined {
    return this.#withDefaults.assetfiletype;
  }

  get phrase(): string | undefined {
    return this.#withDefaults.phrase;
  }

  get imageUrl(): string | undefined {
    return this.#withDefaults.imageurl;
  }

  get events(): string | undefined {
    return this.#withDefaults.events;
  }

  get artistexact(): string | undefined {
    return this.#withDefaults.artistexact;
  }

  // The array getters below return the schema's shared empty list
  // when the search states nothing, rather than a fresh `[]` each read. That
  // is why they are readonly: the list is shared, so pushing to one would be
  // pushing to every search at once.
  get locations(): readonly string[] {
    return this.#withDefaults.locations;
  }

  get specificpeople(): readonly string[] {
    return this.#withDefaults.specificpeople;
  }

  get orientations(): readonly OrientationEnum[] {
    return this.#withDefaults.orientations;
  }

  get numberofpeople(): readonly NumberOfPeopleEnum[] {
    return this.#withDefaults.numberofpeople;
  }

  get ageofpeople(): readonly AgeOfPeopleEnum[] {
    return this.#withDefaults.ageofpeople;
  }

  get ethnicity(): readonly EthnicityEnum[] {
    return this.#withDefaults.ethnicity;
  }

  get compositions(): readonly CompositionSearchFilterEnum[] {
    return this.#withDefaults.compositions;
  }

  get filmdefinition(): readonly FilmDefinitionSearchFilterEnum[] {
    return this.#withDefaults.filmdefinition;
  }

  get framerate(): readonly FrameRateEnum[] {
    return this.#withDefaults.framerate;
  }

  get viewpoint(): readonly ViewPointEnum[] {
    return this.#withDefaults.viewpoint;
  }

  get imagetechnique(): readonly ImageTechniqueEnum[] {
    return this.#withDefaults.imagetechnique;
  }

  get videotemplatecategory(): readonly VideoTemplateCategoryEnum[] {
    return this.#withDefaults.videotemplatecategory;
  }

  get videotemplateappversion(): readonly VideoTemplateAppVersionEnum[] {
    return this.#withDefaults.videotemplateappversion;
  }

  get videodurationmin(): string | undefined {
    return this.#withDefaults.videodurationmin;
  }

  get videodurationmax(): string | undefined {
    return this.#withDefaults.videodurationmax;
  }

  get mood(): ColorMoodEnum | undefined {
    return this.#withDefaults.mood;
  }

  get color(): string | undefined {
    return this.#withDefaults.color;
  }

  get colorIndex(): string | undefined {
    // eslint-disable-next-line no-underscore-dangle
    return this.#withDefaults._colorindex;
  }

  get istockcollection(): IstockCollectionSearchFilterEnum {
    return this.#withDefaults.istockcollection;
  }

  get sort(): SortEnum {
    return this.#withDefaults.sort;
  }

  get editorialproducts(): EditorialProductEnum {
    return this.#withDefaults.editorialproducts;
  }

  /**
   * The individual licenses the search asks for. Empty when it asks for none.
   *
   * Read out of the token rather than stored apart from it: `rf,rm` is one
   * value everywhere it travels, and a rule that weighs the licenses one at a
   * time wants them one at a time.
   */
  get licenses(): LicenseSearchFilterEnum[] {
    return licensesOf(this.#withDefaults.license);
  }

  /**
   * What the license filter is set to, or `undefined` when the search asks for
   * no license. The one token the url, the search service and the filter
   * panel's Select exchange, which is why it is what is stored.
   *
   * Already in the enum's spelling: `normalizeLicenseToken` settles the order,
   * any repeat and any unknown member when the value is parsed, so there is
   * nothing to reconcile on read.
   */
  get license(): LicenseSearchFilterEnum | undefined {
    return this.#withDefaults.license;
  }

  get page(): number {
    return this.#withDefaults.page;
  }

  get recency(): RecencyEnum {
    return this.#withDefaults.recency;
  }

  get begindate(): string | undefined {
    return this.#withDefaults.begindate;
  }

  get enddate(): string | undefined {
    return this.#withDefaults.enddate;
  }

  get imagesize(): ImageSizeSearchFilterEnum | undefined {
    return this.#withDefaults.imagesize;
  }

  get releasestatus(): ReleaseStatusSearchFilterEnum | undefined {
    return this.#withDefaults.releasestatus;
  }

  get contributor(): string | undefined {
    return this.#withDefaults.contributor;
  }

  get recentlyviewed(): string | undefined {
    return this.#withDefaults.recentlyviewed;
  }

  get morelikethis(): string | undefined {
    return this.#withDefaults.morelikethis;
  }

  get stackid(): string | undefined {
    return this.#withDefaults.stackid;
  }

  get curatedset(): string | undefined {
    return this.#withDefaults.curatedset;
  }

  get alloweduse(): AllowedUseSearchFilterEnum | undefined {
    return this.#withDefaults.alloweduse;
  }

  get phraseprocessing(): PhraseProcessingSearchFilterEnum | undefined {
    return this.#withDefaults.phraseprocessing;
  }

  get offlinecontent(): OfflineContentSearchFilterEnum | undefined {
    return this.#withDefaults.offlinecontent;
  }

  get groupByEvent(): boolean {
    return this.#withDefaults.groupbyevent === 'true';
  }

  get embeddable(): string | undefined {
    return this.#withDefaults.embeddable;
  }

  /**
   * Whether the search asks to exclude nudity.
   *
   * Compares against `'true'` rather than testing for a value, because the
   * switch writes `'false'` when the visitor turns it off (`offValue` in
   * `excludeNudityFilter`). A presence test reads that as on.
   */
  get excludenudity(): boolean {
    return this.#withDefaults.excludenudity === 'true';
  }

  get istockexclusive(): boolean {
    return this.#withDefaults.istockexclusive !== undefined;
  }

  // Prototype methods, unlike the arrow class fields above: these return new
  // instances freely, so per-instance closures would allocate on every chained
  // call, and none of them is ever detached from its instance.

  /**
   * Reads a parameter by its schema name, as the schema stores it. A parameter
   * the search never stated reads as the value its schema row defaults to.
   *
   * This is the raw view, not the generic form of the getters above, and for
   * four parameters the two deliberately disagree:
   *
   * | | `get(name)` | getter |
   * | --- | --- | --- |
   * | `groupbyevent` | `'true'` / `'false'` | `groupByEvent`: boolean |
   * | `excludenudity` | `'true'` / `'false'` | `excludenudity`: boolean |
   * | `istockexclusive` | the token, or absent | `istockexclusive`: present? |
   * | `license` | `'rf,rm'` | `licenses`: `['rf', 'rm']` |
   *
   * Both views are wanted. The url and the search service exchange the stored
   * form, and tracking reports the value the visitor's query actually carried,
   * so `get` must not coerce. A rule asking what the search *means* wants the
   * getter.
   *
   * The trap is the first two rows: `'false'` is a non-empty string, so
   * `if (searchParameters.get('groupbyevent'))` is true for a search that is
   * not grouped by event. Ask `searchParameters.groupByEvent` for the meaning;
   * reach for `get` only when the stored form is what you want, or when the
   * name is a variable.
   */
  get<N extends SearchParameterName>(
    name: N
  ): (SchemaDefaults & SearchParameterValues)[N] {
    return this.#withDefaults[name];
  }

  /**
   * Whether the search states `name`, as opposed to running under the value its
   * schema row defaults to.
   */
  states(name: SearchParameterName): boolean {
    return name in this.#stated;
  }

  /** Replaces one parameter's value outright, rather than toggling it. */
  set<N extends SearchParameterName>(
    name: N,
    value: SearchParameterValues[N] | undefined
  ): SearchParameters {
    return new SearchParameters(setValue(this.#stated, name, value));
  }

  /** Sets each named parameter; `undefined` clears instead of setting. */
  with(changes: Partial<SearchParameterValues>): SearchParameters {
    return new SearchParameters(withValues(this.#stated, changes));
  }

  /** Removes each named parameter, restoring its schema default if it has one. */
  clear(...names: SearchParameterName[]): SearchParameters {
    return new SearchParameters(clearedValues(this.#stated, names));
  }

  /** Adds `value` if absent, removes it if present. */
  toggle<N extends SearchParameterName>(
    name: N,
    value: SearchParameterElement<N>
  ): SearchParameters {
    return new SearchParameters(toggledValue(this.#stated, name, value));
  }

  /** Whether `toggle` would remove `value` rather than add it. */
  isSelected<N extends SearchParameterName>(
    name: N,
    value: SearchParameterElement<N>
  ): boolean {
    return isValueSelected(this.#stated, name, value);
  }
}
